import React, { useEffect, useState, useMemo, useCallback, ReactNode } from 'react'
import { Dropdown, Menu } from 'antd'
import { useInterval } from 'ahooks'
import { delay } from '@/utils/common'
import styles from './qrcode-dropdown.module.less'

interface QRCodeDropdownProps {
  slogan: string;
  onGetQrCode: () => Promise<{ qrCodeUrl: string }>;
  children?: ReactNode;
}

const QRCODE_EXPIRE_TIME = 60

const QRCodeDropdown = (props: QRCodeDropdownProps) => {
  const [visible, setVisible] = useState(false)
  const { slogan, onGetQrCode, children } = props
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [valid, setValid] = useState(false)
  const [count, setCount] = useState(QRCODE_EXPIRE_TIME)
  const [interval, setInterval] = useState<number | undefined>()

  const clear = useInterval(() => {
    setCount(count - 1)
    setValid(count > 0)
  }, interval)

  const fetchQrCode = useCallback(() => {
    if (loading) {
      console.log('wait ...')
      return
    }
    setLoading(true)
    onGetQrCode().then(res => {
      setUrl(res.qrCodeUrl)
      setValid(true)
      setCount(QRCODE_EXPIRE_TIME)
      setInterval(1000)
    }).catch(() => {
      setUrl('')
      setValid(false)
      delay(1500).then(() => { fetchQrCode() })
    }).finally(() => {
      setLoading(false)
    })
  }, [loading])

  const resetState = () => {
    setValid(false)
    setUrl('')
    clear()
    setInterval(undefined)
    setCount(QRCODE_EXPIRE_TIME)
  }

  const onVisibleChange = useCallback((visible: boolean) => {
    setVisible(visible)
    if (!visible) {
      resetState()
    }
  }, [])

  useEffect(() => {
    if (!valid && !loading && visible) {
      setValid(true)
      clear()
      fetchQrCode()
    }
  }, [valid, loading, visible])

  const qrCode = useMemo(() => {
    if (!visible) return null
    if (loading) {
      return (
        <div className={styles.loading}>二维码加载中...</div>
      )
    }

    if (!valid) {
      return (
        <div className={styles.invalid}>
          <p className={styles.text}>二维码已失效</p>
        </div>
      )
    }
    if (url) {
      return (
        <img src={url} alt="二维码" width={176} height={176} />
      )
    }
    return null
  }, [visible, loading, valid, url])

  const qrcodeOverlay = (
    <Menu className="header-qrcode-overlay">
      <Menu.Item>
        <div className={styles['qrcode-body']}>
          <div className={styles['download-app']}>
            <div className={styles.title}>网络门店APP</div>
              <div className={styles.slogan}>扫一扫下载 · 手机看数据</div>
              <div className={styles['drawer-withoutBG']}>
                <img src='https://pic3.58cdn.com.cn/nowater/frs/n_v324dd6900b13e4f06b12249ab0d5228ac.png' alt="二维码" width={168} height={168} />
              </div>
            </div>
          <div>
            <div className={styles.title}>网络门店微信公众号</div>
            <div className={styles.slogan}>微信扫码关注 · 重要提醒不错过</div>
            <div className={styles.drawer}>
              { qrCode }
            </div>
          </div>
        </div>
      </Menu.Item>
    </Menu>
  )

  return (
    <Dropdown
      overlayStyle={{ position: 'fixed', zIndex: 9999 }}
      overlay={qrcodeOverlay}
      placement={'bottomCenter'}
      onVisibleChange={onVisibleChange}
      arrow
    >
      {
        children || (
          <a>
            <i className="iconbroker-menu-phone" />
          </a>
        )
      }
    </Dropdown>
  )
}

export { QRCodeDropdown }
